import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { usePatchTemplate } from "../../../hook/templates";
import { TemplateNavigationPath } from "../../../models/templates";
import { queryClient } from "../../../utils/http/queryClient";
import { fetchTemplate } from "../../../utils/http/templates";

function TemplateConfigParameters({
  path,
  version,
  readOnly,
}: {
  path: TemplateNavigationPath;
  version: string;
  readOnly: boolean;
}) {
  const [newParameter, setNewParameter] = useState("");
  const [parameters, setParameters] = useState<Record<string, string>>({} as Record<string, string>);

  const [timeoutSaveDraftId, setTimeoutSaveDraftId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const patchMutation = usePatchTemplate(path, version);

  const { data: template = null } = useQuery({
    queryKey: ["mailTemplates", path.namespace, path.name, version],
    queryFn: async ({ signal }) => {
      return await fetchTemplate({ signal, key: path, version: version });
    },
  });

  useEffect(() => {
    let newParameters = {};
    if (template?.config != null) {
      newParameters = template.config.parameters as Record<string, string>;
    }
    setParameters({ ...(newParameters as Record<string, string>) });
  }, [template]);

  const updateParameters = (parameters: Record<string, string>) => {
    setParameters({ ...parameters });

    // Only send updates to the BE if nothing was changed in the past second (to avoid HTTP traffic on each keystroke)
    if (timeoutSaveDraftId) {
      clearTimeout(timeoutSaveDraftId);
    }
    const timeoutId = setTimeout(async () => {
      await patchMutation.mutateAsync({
        key: path,
        dataToUpdate: {
          "config.parameters": parameters,
        },
      });

      // Make sure the preview is rerendered
      queryClient.resetQueries({
        queryKey: ["templateRender"],
      });
    }, 1000);
    setTimeoutSaveDraftId(timeoutId);
  };

  const addParameter = () => {
    if (newParameter && !parameters[newParameter]) {
      setParameters({ ...parameters, [newParameter]: "" });
      updateParameters({ ...parameters, [newParameter]: "" });
      setNewParameter("");
    }
  };

  const updateParameterDefault = (param: string, value: string) => {
    const newParameters = { ...parameters, [param]: value };
    setParameters(newParameters);
    updateParameters(newParameters);
  };

  const removeParameter = (param: string) => {
    const newParameters = { ...parameters };
    delete newParameters[param];
    setParameters(newParameters);
    updateParameters({ ...newParameters });
  };

  return (
    <Container className="mt-3">
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm="4" className="text-end">
          Current parameters:
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={`tooltip-bottom`}>
                The parameters your block needs to function properly. These parameters can be used in the block and can
                be set when the block is used in a template. You can use the parameters in the block by using the syntax{" "}
                <code>&#123;&#123; parameterName &#125;&#125;</code>.
              </Tooltip>
            }
          >
            <i className="bi bi-info-circle ms-1"></i>
          </OverlayTrigger>
        </Form.Label>
        <Col sm="8" className="mt-2">
          {Object.keys(parameters).length === 0 && <span className="text-muted">No parameters set yet.</span>}

          {Object.keys(parameters).length > 0 && (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th></th>
                  <th>Parameter</th>
                  <th className="ps-3">Default value</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(parameters).map((param) => (
                  <tr key={param}>
                    <td className="align-middle">
                      {!readOnly && <i className="bi bi-trash me-2" onClick={() => removeParameter(param)}></i>}
                    </td>
                    <td className="align-middle">{param}</td>
                    <td className="align-middle">
                      <Form.Control
                        type="text"
                        id="name"
                        disabled={readOnly}
                        value={parameters[param]}
                        onChange={(e) => updateParameterDefault(param, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            updateParameterDefault(param, (e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        // isInvalid={errors.name ? true : false}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Col>
      </Form.Group>
      {!readOnly && (
        <Form.Group as={Row} className="mb-3 align-items-center">
          <Form.Label column sm="4" className="text-end"></Form.Label>
          <Col sm="5">
            <Form.Control
              type="text"
              id="name"
              value={newParameter}
              onChange={(e) => setNewParameter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addParameter();
                }
              }}
              // isInvalid={errors.name ? true : false}
            />
          </Col>
          <Col sm="3">
            <Form.Group as={Col} className="p-0 ps-1 pe-1">
              <Button onClick={addParameter}>Add</Button>
            </Form.Group>
          </Col>
        </Form.Group>
      )}
    </Container>
  );
}

export default TemplateConfigParameters;
